"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Info, Globe, MapPin, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function FindRightCenter() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const content = {
    vi: {
      heading: "BẠN ĐANG TÌM CƠ SỞ PHÙ HỢP CHO CON?",
      subheading: "Chọn cơ sở thuận tiện cho gia đình, sau đó đăng ký tư vấn để tìm hiểu chương trình phù hợp với độ tuổi và mục tiêu học tập của con.",
      note: "Lưu ý: Các chương trình đào tạo có thể khác nhau tùy thuộc vào từng cơ sở.",
      cta: "ĐĂNG KÝ TƯ VẤN",
      switchLabel: "English"
    },
    en: {
      heading: "Looking for the right center for your child?",
      subheading: "Choose the center that's most convenient for your family, then register for a consultation to find the right program for your child's age and goals.",
      note: "Note: Program availability may vary by location.",
      cta: "Book a Consultation",
      switchLabel: "Tiếng Việt"
    }
  };
  const activeContent = content[lang];
  return <section id="find-the-right-center" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Background Ambient Glow (Decoration Kit) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[18rem] bg-secondary/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 right-1/4 w-80 h-80 bg-accent/15 rounded-full blur-3xl" />
      </div>

      <div className="container max-w-6xl mx-auto px-4 relative z-10">
        <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} whileInView={{
        opacity: 0.999,
        scale: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }}>
          <Card className="relative bg-card text-card-foreground border border-border shadow-xl rounded-2xl overflow-hidden py-0">
            {/* 1px Lime-green spec rail / top border stripe */}
            <div className="h-1.5 w-full bg-secondary" />

            <CardContent className="p-6 sm:p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                
                {/* Content Column */}
                <div className="lg:col-span-7 flex flex-col items-start text-left">
                  {/* Language Switcher Pill */}
                  <div className="self-end lg:self-start mb-6">
                    <Button variant="outline" size="sm" onClick={() => setLang(lang === "vi" ? "en" : "vi")} className="rounded-full text-xs font-medium border-border/80 text-muted-foreground hover:text-foreground gap-1.5 px-3 py-1 h-8" aria-label="Switch language">
                      <Globe className="size-3.5 text-secondary" />
                      <span>{activeContent.switchLabel}</span>
                    </Button>
                  </div>

                  {/* Main Headline */}
                  <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground max-w-2xl mb-4 leading-tight">
                    {activeContent.heading}
                  </h2>

                  {/* Supporting Copy */}
                  <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mb-6 leading-relaxed font-default">
                    {activeContent.subheading}
                  </p>

                  {/* Program Availability Disclaimer Note */}
                  <div className="w-full max-w-xl bg-muted/70 border border-border/70 rounded-xl px-4 py-3 mb-8 flex items-center justify-start gap-2.5 text-xs sm:text-sm text-muted-foreground">
                    <Info className="size-4 text-secondary shrink-0" />
                    <span>{activeContent.note}</span>
                  </div>

                  {/* Primary CTA Button */}
                  <motion.div whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className="w-full sm:w-auto">
                    <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold text-base px-8 py-6 rounded-xl gap-2 transition-all duration-200">
                      <Link to="/FreeTrialContactPage">
                        <MapPin className="size-5 shrink-0" />
                        <span>{activeContent.cta}</span>
                        <ArrowRight className="size-4 shrink-0" />
                      </Link>
                    </Button>
                  </motion.div>
                </div>

                {/* Technical Photo Collage Column */}
                <div className="lg:col-span-5 relative">
                  <div className="relative rounded-xl p-2 bg-[#0d1629] border border-border shadow-2xl">
                    
                    {/* Technical Header Overlay Badge */}
                    <div className="flex items-center justify-between px-3 py-2 bg-[#070d1a] rounded-lg mb-2 text-xs font-mono text-muted-foreground border border-white/10">
                      <div className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                        <span className="size-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span>ROBOTO LAB CENTER</span>
                      </div>
                      <span className="text-white/60">HQ INTERIOR SPEC</span>
                    </div>

                    {/* Main Showcase Interior Image */}
                    <div className="relative rounded-lg overflow-hidden border border-white/10 aspect-[4/3] group">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_main.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_main.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_main.png-wvc-sizes" alt="ROBOTO Academy Center High-Tech Interior Lab" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#070d1a]/80 via-transparent to-transparent" />
                      
                      {/* Technical Blueprint Badge Overlay */}
                      <div className="absolute bottom-3 left-3 bg-[#070d1a]/90 backdrop-blur-md border border-white/15 px-3 py-1.5 rounded-md text-xs font-mono text-white flex items-center gap-2 shadow-lg">
                        <Sparkles className="size-3.5 text-secondary" />
                        <span>STEM & Robotics Innovation Space</span>
                      </div>
                    </div>

                    {/* Supporting Secondary Photos Grid */}
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div className="relative rounded-lg overflow-hidden border border-white/10 aspect-[16/10] group">
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_lab.png-wvc-sizes" alt="ROBOTO Student Engineering Workstation" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-[#070d1a]/20" />
                        <div className="absolute bottom-1.5 left-2 text-[10px] font-mono text-white/90 bg-black/60 px-1.5 py-0.5 rounded">
                          Workstations
                        </div>
                      </div>

                      <div className="relative rounded-lg overflow-hidden border border-white/10 aspect-[16/10] group">
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_arena.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_arena.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_center_interior_arena.png-wvc-sizes" alt="ROBOTO Competition Test Arena" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-[#070d1a]/20" />
                        <div className="absolute bottom-1.5 left-2 text-[10px] font-mono text-white/90 bg-black/60 px-1.5 py-0.5 rounded">
                          Testing Arena
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}